import { useContext, useEffect, useState, Suspense } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary, useRollbarPerson } from '@rollbar/react'
import NavMenu from 'components/NavMenu/NavMenu'
import Header from 'components/Header/Header'
import { isMobile } from 'helpers/styles'
import style from './PageWrapper.module.scss'
import Back from 'components/common/Back/Back'
import NavMenuFooter from 'components/NavMenuFooter/NavMenuFooter'
import NotFoundPage from 'components/NotFoundPage/NotFoundPage'
import ClientContext from 'contexts/ClientContext'
import colors from 'styles/colors.scss'
import CoLabelPopup from 'components/CoLabelPopup/CoLabelPopup'
import api from 'helpers/api'
import OwenAssistant from 'components/OwenAssistant/OwenAssistant'

const PageWrapper = () => {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  // Accès temporaire (lien WhatsApp, sans login) à un sinistre précis : on court-circuite
  // tout le shell authentifié (nav, header, contexte client) et on laisse la page gérer
  // elle-même son authentification via le token. Scopé strictement à /claims/:id?token=...
  const isClaimAccessRoute = /^\/claims\/[^/]+$/.test(pathname) && !!new URLSearchParams(search).get('token')
  const [ contract, setContract ] = useState({})
  const [ colabelOptions, setColabelOptions ] = useState({})
  const [ adhesions, setAdhesions ] = useState([])
  const [ userAdhesion, setUserAdhesion ] = useState(null)
  const [isMenuRendered, setIsMenuRendered] = useState(!isMobile())
  const client = useContext(ClientContext)
  const accessToken = localStorage.getItem('accessToken')
  const isAuthRoute = [
    'sign-in',
    'sign-up',
    'sign-up-confirm',
    'reset-password',
    'reset-password/requested'
  ].includes(pathname.slice(1))

  useRollbarPerson(client)

  useEffect(() => {
    if (pathname === '/') navigate('contracts')

    const getDatas = async () => {
      try {
        const colabelRes = await api.get({ url: '/adhesions/colabel' })
        if (colabelRes?.adhesions) setColabelOptions(colabelRes)
      } catch (err) {}
      try {
        const adhesionRes = await api.get({ url: '/adhesions/client' })
        const list = adhesionRes?.adhesions || []
        if (list.length) {
          setAdhesions(list)
          setUserAdhesion(list.find(a => a.adhesionStatus === 'active') || list[0])
        }
      } catch (err) {}
    }
    if (accessToken && !colabelOptions.adhesions) getDatas()
  }, [pathname, navigate, accessToken, colabelOptions.adhesions])

  window.onresize = () => {
    setIsMenuRendered(!isMobile())
  }

  const onNavItemClick = path => {
    if (isMenuRendered) setIsMenuRendered(false)
    navigate(path)
  }

  const onToggleMenu = () => {
    setIsMenuRendered(!isMenuRendered)
  }

  const AuthRoute = () => (
    <div className={style.auth}>
      <Header darkBg styles={{ position: 'absolute', top: 0 }} />
      <Outlet />
    </div>
  )

  return (
    <Suspense fallback={''}>
      {isClaimAccessRoute
        ? <Outlet />
        : !isAuthRoute
        ? !accessToken
          ? <Navigate to={'/sign-in'} replace />
          : <ErrorBoundary fallbackUI={NotFoundPage}>
            {colabelOptions.adhesions &&
              <ClientContext.Provider value={{
                ...contract,
                setContract,
                ...colors,
                ...colabelOptions
              }}>
                <div className={style['page-wrapper']}>
                  {colabelOptions.popup && <CoLabelPopup />}
                  {isMenuRendered && <NavMenu onNavItemClick={onNavItemClick} />}
                  <div className={style.content}>
                    <Header />
                    <Back className={style.back} />
                    <div className={style.outlet}>
                      <Outlet />
                    </div>
                    {isMobile() && <NavMenuFooter className onNavItemClick={onNavItemClick} onToggleMenu={onToggleMenu} />}
                  </div>
                </div>
                {process.env.REACT_APP_OWEN_ASSISTANT === 'true' && <OwenAssistant userAdhesion={userAdhesion} adhesions={adhesions} />}
              </ClientContext.Provider>
            }
          </ErrorBoundary>
        : accessToken
          ? <Navigate to={'/contracts'} replace />
          : <AuthRoute />
      }
    </Suspense>
  )
}

export default PageWrapper
